import { faInfoSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Box,
    Card,
    CardContent,
    Stack,
    Tooltip,
    useTheme,
} from "@vapor/react-material";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { FulfilmentStatus } from "../../../../../core/api/use-get-fulfilment-detail/FulfilmentStatus";
import { getFulfilmentColorAndVariantByStatus } from "../../../../../utils/getColorsByStatus";
import { FulfilmentStatusChip } from "../../../../components/fulfilment-status-chip/FulfilmentStatusChip";

interface FulfilmentListHeaderKPICardProps {
    numberOfWarnings: number;
    fulfilmentStatus: FulfilmentStatus;
    isSelected: boolean;
    loading: boolean;
    numberOfFulfilmentStatus: number;
    onClickKPI: (fulfilmentStatus: FulfilmentStatus) => unknown;
}

export const FulfilmentListHeaderKPICard = ({
    numberOfWarnings,
    fulfilmentStatus,
    isSelected = false,
    loading,
    numberOfFulfilmentStatus,
    onClickKPI,
}: FulfilmentListHeaderKPICardProps): ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const borderColor = useMemo(
        () =>
            getFulfilmentColorAndVariantByStatus(fulfilmentStatus, theme)
                .themeColor,
        [fulfilmentStatus, theme],
    );

    return (
        <Card
            elevation={isSelected ? 4 : 0}
            onClick={() => !loading && onClickKPI(fulfilmentStatus)}
            sx={{
                ":hover": {
                    boxShadow: 4,
                },
                border: isSelected
                    ? `1px solid ${theme.palette.primary.interactiveDefault}`
                    : `1px solid ${theme.palette.primary.gainsboro}`,
                borderLeft: `4px solid ${borderColor}`,
                cursor: "pointer",
                width: "100%",
            }}
            style={{
                backgroundColor: isSelected
                    ? theme.palette.primary.whiteSmoke
                    : undefined,
            }}
        >
            <CardContent style={{ paddingBottom: "8px", paddingTop: "8px" }}>
                <LoadingWrapper
                    loading={loading}
                    variant="backdrop"
                    skeletonProps={{ rowsNumber: 1 }}
                >
                    <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                            <Stack direction="row" spacing={2}>
                                <Typography
                                    color={theme.palette.primary.textTitleColor}
                                    variant="titleMedium"
                                >
                                    {numberOfFulfilmentStatus}
                                </Typography>
                            </Stack>
                            <Stack
                                alignItems="center"
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Tooltip
                                    title={
                                        <Stack spacing={2}>
                                            <Typography
                                                color={
                                                    theme.palette.primary
                                                        .surfaceDefault
                                                }
                                                variant="caption"
                                            >
                                                {t(
                                                    `views.home.components.fulfilment_list_header_kpi_card.${fulfilmentStatus.toLowerCase()}.tooltip.title`,
                                                    {
                                                        status: fulfilmentStatus,
                                                    },
                                                )}
                                            </Typography>
                                            <Typography
                                                color={
                                                    theme.palette.primary
                                                        .surfaceDefault
                                                }
                                                variant="captionSmall"
                                            >
                                                {t(
                                                    `views.home.components.fulfilment_list_header_kpi_card.${fulfilmentStatus.toLowerCase()}.tooltip.text`,
                                                    {
                                                        status: fulfilmentStatus,
                                                    },
                                                )}
                                            </Typography>
                                        </Stack>
                                    }
                                    placement="top"
                                >
                                    <FontAwesomeIcon
                                        color={
                                            theme.palette.primary.textTitleColor
                                        }
                                        icon={faInfoSquare}
                                    />
                                </Tooltip>
                            </Stack>
                        </Stack>

                        <Box>
                            <FulfilmentStatusChip
                                numberOfWarnings={numberOfWarnings}
                                plural
                                status={fulfilmentStatus}
                            />
                        </Box>
                    </Stack>
                </LoadingWrapper>
            </CardContent>
        </Card>
    );
};
